import { useQuery, useMutation, useQueryClient } from 'react-query';
import {
    ApiError,
    getCreateThreadInfo,
    getUsersAlertThreadResponse,
    createThreadPostBody,
    addMessageToThreadPUT,
    addUserToThreadPut,
    PUTSendAlertThreadMessage,
    getThreadsPagePopulateResponse,
    getThreadMessagesResponse,
    getReadSingleAlertThreadResponse,
} from 'src/modules/generated-api-clients/sql';
import { useAuthenticator } from '@aws-amplify/ui-react';
import { useApiSqlContext } from 'src/modules/contexts/ApiSqlContext';

// This query is for getting the info options for compose email
export const useQueryGetThreadsInfo = (options = {}) => {
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useQuery<getCreateThreadInfo, ApiError>(
        ['threads-info', token],
        async () => {
            if (!client) {
                throw new Error('API client is not initialized');
            }

            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            try {
                const response =
                    await client.default.getAdminPortalEmailsAndThreadsCreateThreadsInfo(
                        token
                    );
                return response;
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    console.error('Error fetching alerts:', error);
                }
                throw error;
            }
        },
        {
            enabled: !!token,
            ...options,
        }
    );
};

//this query display the main threads page
export const useQueryGetPopulateThreads = (options = {}) => {
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useQuery<getThreadsPagePopulateResponse, ApiError>(
        ['threads-page', token],
        async () => {
            if (!client) {
                throw new Error('API client is not initialized');
            }

            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            try {
                const response =
                    await client.default.getAdminPortalEmailsAndThreadsThreadsPage(
                        token
                    );
                return response;
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    console.error('Error fetching alerts:', error);
                }
                throw error;
            }
        },
        {
            enabled: !!token,
            ...options,
        }
    );
};

//this query is being called when we select a thread
export const useQueryGetThreadsMessages = (thread_id: string, options = {}) => {
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();
    const queryClient = useQueryClient();

    return useQuery<getThreadMessagesResponse, ApiError>(
        ['threads-messages', token, thread_id],
        async () => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (!token) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }
            try {
                const response =
                    await client.default.getAdminPortalEmailsAndThreadsThread(
                        thread_id,
                        token
                    );
                return response;
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${error.message}\nBODY: ${JSON.stringify(
                            error.body
                        )}`
                    );
                } else {
                    console.error('Error fetching alerts:', error);
                }
                throw error;
            }
        },
        {
            enabled: !!token,
            onSuccess: () => {
                queryClient.invalidateQueries(['threads-info']);
                queryClient.invalidateQueries(['threads-page']);
            },
            ...options,
        }
    );
};

// this mutation is to submit the composed email
export const useMutationPostCreateThread = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useMutation<unknown, ApiError, createThreadPostBody>(
        async requestBody => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            return client.default.postAdminPortalEmailsAndThreadsThread(
                token,
                requestBody
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('threads-page');
            },
            onError: (error: ApiError) => {
                // Handle ApiError specifically
                console.error(
                    `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                        error.message
                    }\nBODY: ${JSON.stringify(error.body)}`
                );
            },
        }
    );
};

// this mutation is to reply to a thread
export const useMutationPutMessageThread = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useMutation<unknown, ApiError, addMessageToThreadPUT>(
        async requestBody => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            return client.default.putAdminPortalEmailsAndThreadsThread(
                token,
                requestBody
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('threads-page');
                queryClient.invalidateQueries('threads-messages');
            },
            onError: (error: ApiError) => {
                // Handle ApiError specifically
                console.error(
                    `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                        error.message
                    }\nBODY: ${JSON.stringify(error.body)}`
                );
            },
        }
    );
};

// this mutation is to add a user to the generic email thread
export const useMutationPutAddUserThread = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useMutation<unknown, ApiError, addUserToThreadPut>(
        async requestBody => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }
            return client.default.putAdminPortalEmailsAndThreadsThreadsUsers(
                token,
                requestBody
            );
        },
        {
            onSuccess: (_data, variables) => {
                // Use the thread_id from variables to invalidate queries
                queryClient.invalidateQueries([
                    'threads-messages',
                    token,
                    variables.thread_id,
                ]);
                queryClient.invalidateQueries(['threads-page']);
                queryClient.invalidateQueries(['threads-info']);
            },
            onError: (error: ApiError) => {
                console.error(
                    `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${error.message}\nBODY: ${JSON.stringify(error.body)}`
                );
            },
        }
    );
};

// ==================================== ALERT THREADS ====================================

// this query to get single alert thread
export const useQueryGetSingleAlertThread = (
    thread_id: string,
    options = {}
) => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useQuery<getReadSingleAlertThreadResponse, ApiError>(
        ['threads-single-alert', token, thread_id],
        async () => {
            if (!client) {
                throw new Error('API client is not initialized');
            }

            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            try {
                const response =
                    await client.default.getAdminPortalEmailsAndThreadsReadSingleAlert(
                        thread_id,
                        token
                    );
                return response;
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    console.error('Error fetching alerts:', error);
                }
                throw error;
            }
        },
        {
            enabled: !!token,
            onSuccess: () => {
                queryClient.invalidateQueries('threads-page');
            },
            ...options,
        }
    );
};

// this query to get user list for specific alert
export const useQueryGetAlertThreadUserList = (
    alert_id: string,
    options = {}
) => {
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useQuery<getUsersAlertThreadResponse, ApiError>(
        ['threads-alert-user-list', token, alert_id],
        async () => {
            if (!client) {
                throw new Error('API client is not initialized');
            }

            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            try {
                const response =
                    await client.default.getAdminPortalEmailsAndThreadsAlertThreads(
                        token,
                        alert_id
                    );
                return response;
            } catch (error: unknown) {
                if (error instanceof ApiError) {
                    console.error(
                        `ApiError -> URL: ${error.url}\nSTATUS: ${error.status}\nMESSAGE: ${
                            error.message
                        }\nBODY: ${JSON.stringify(error.body)}`
                    );
                } else {
                    console.error('Error fetching alerts:', error);
                }
                throw error;
            }
        },
        {
            enabled: !!token,
            ...options,
        }
    );
};

//this mutation is to add a msg to alert thread
export const useMutationPutAlertMsgThread = () => {
    const queryClient = useQueryClient();
    const { client } = useApiSqlContext();
    const { user } = useAuthenticator(context => [context.user]);
    const token = user.getSignInUserSession()?.getIdToken().getJwtToken();

    return useMutation<unknown, ApiError, PUTSendAlertThreadMessage>(
        async requestBody => {
            if (!client) {
                throw new Error('API client is not initialized');
            }
            if (token === undefined) {
                throw new Error(
                    'AWS Amplify user attributes cannot be undefined'
                );
            }

            return client.default.putAdminPortalEmailsAndThreadsAlertThreads(
                token,
                requestBody
            );
        },
        {
            onSuccess: () => {
                queryClient.invalidateQueries('threads-single-alert');
            },
        }
    );
};
